import type { VoiceCreditUsageByAgent } from "@repo/app-types/entities/creditUsage";
import { MetronomeErrorCode } from "@repo/app-types/entities/metronome";
import { TableSkeleton } from "@repo/ui/Skeletons/skeletons";
import type { FC } from "react";
import { useVoiceUsageQuery } from "../../../api/entities/company.js";
import { SettingsTable } from "../shared/settingsTable.js";
import { SettingsTableBodySection } from "../shared/settingsTableBodySection.js";
import { SettingsTableDataCell } from "../shared/settingsTableDataCell.js";
import { SettingsTableHeaderCell } from "../shared/settingsTableHeaderCell.js";
import { SettingsTableHeaderSection } from "../shared/settingsTableHeaderSection.js";
import { useCreditUsageSection } from "./creditUsageSection.js";
import { formatWholeNumber } from "./usageFormatting.js";

interface VoiceCreditsSectionProps {
  periodOffset: number;
}

/**
 * The voice agent usage breakdown on the billing page: this period's voice
 * spend per agent, for the period the page-level header selects. Voice usage
 * draws from the single AI-credit balance shown by the AI credits section, so
 * this is a read-only lens with no balance or purchase of its own.
 */
export const VoiceCreditsSection: FC<VoiceCreditsSectionProps> = (props) => {
  const { periodOffset } = props;
  const state = useCreditUsageSection(useVoiceUsageQuery, periodOffset);
  const { selectedUsage, usageErrorCode, isUsageError, isUsageLoading } = state;

  // Nothing to show a company that has never been on a Metronome contract.
  if (usageErrorCode === MetronomeErrorCode.NOT_FOUND && periodOffset === 0) {
    return null;
  }
  if (isUsageError) {
    return null;
  }

  return (
    <section>
      <h2 className="text-base font-semibold text-foreground">
        Voice agent usage
      </h2>
      <div className="mt-4 space-y-4">
        <UsageByAgent
          agents={selectedUsage?.agents ?? []}
          isLoading={isUsageLoading}
        />
        {!isUsageLoading && (selectedUsage?.agents.length ?? 0) === 0 ? (
          <p className="text-sm text-muted-foreground">
            No voice agent calls this period
          </p>
        ) : null}
      </div>
    </section>
  );
};

interface UsageByAgentProps {
  agents: readonly VoiceCreditUsageByAgent[];
  isLoading: boolean;
}

const UsageByAgent: FC<UsageByAgentProps> = ({ agents, isLoading }) => (
  <SettingsTable>
    <SettingsTableHeaderSection>
      <tr>
        <SettingsTableHeaderCell className="w-[70%]">
          Voice agent
        </SettingsTableHeaderCell>
        <SettingsTableHeaderCell className="w-[30%] text-right">
          AI credits used
        </SettingsTableHeaderCell>
      </tr>
    </SettingsTableHeaderSection>
    <SettingsTableBodySection>
      {isLoading ? <TableSkeleton /> : null}
      {agents.map((agent) => (
        <tr className="hover:bg-gray-50" key={agent.voice_agent_id}>
          <SettingsTableDataCell>
            {agent.voice_agent_name || "Unnamed agent"}
          </SettingsTableDataCell>
          <SettingsTableDataCell className="text-right tabular-nums">
            {formatWholeNumber(agent.credits_used)}
          </SettingsTableDataCell>
        </tr>
      ))}
    </SettingsTableBodySection>
  </SettingsTable>
);
